import { useTranslate } from 'app/[locale]/hooks/useTranslate';
import { PermissionEnum } from './types';
import {
  BaseTeamRole,
  BaseWorkspaceRole,
  TeamRole,
  WorkspaceRoleWithPermissions,
} from 'callabo-api/src';
import { useCallback, useContext } from 'react';
import { AuthContext } from '@/contexts/AuthContext';
import { useWorkspaceId } from '../useWorkspaceHooks';
import { setRoleStore, useRoleStore } from './useRoleStore';
import { useSC } from '@/contexts/useSC';

export const useFetchRoles = () => {
  const { authState } = useContext(AuthContext);
  const workspaceId = useWorkspaceId();

  const isValidSC = useSC(['e', 'i']);
  return useCallback(async () => {
    if (authState.type !== 'signedIn') return;
    const { callaboApiClient } = authState;
    const promises: [Promise<WorkspaceRoleWithPermissions[]>, Promise<TeamRole[]>] = [
      callaboApiClient.workspaceRoles.getWorkspaceRolesV1WorkspacesSlugWorkspaceRolesGet(
        workspaceId
      ),
      callaboApiClient.team.getTeamRolesV1WorkspacesSlugTeamRolesGet(workspaceId),
    ];

    const [workspaceRoles, teamRoles] = await Promise.all(promises);
    if (!isValidSC) {
      const index = teamRoles.findIndex((role) => role.name === 'team_meeting_manager');
      if (index !== -1) {
        teamRoles.splice(index, 1);
      }
    }
    setRoleStore({ workspaceRoles, teamRoles });
  }, [authState.type, workspaceId, isValidSC]);
};

export function useBaseRoleLocale(): (role: BaseWorkspaceRole | string) =>
  | {
      name: string;
      description: string;
    }
  | undefined {
  const { t } = useTranslate();

  const typeLocaleMap: Partial<Record<BaseWorkspaceRole, { name: string; description: string }>> = {
    [BaseWorkspaceRole.WORKSPACE_MANAGER]: {
      name: t.members.ROLE_WORKSPACE_ADMIN,
      description: t.members.ROLE_WORKSPACE_ADMIN_DESC,
    },
    [BaseWorkspaceRole.ARCHIVIST]: {
      name: t.members.ROLE_MEETING_EDITOR,
      description: t.members.ROLE_MEETING_ADMIN_DESC,
    },
    [BaseWorkspaceRole.VIEWER]: {
      name: t.members.ROLE_VIEWER,
      description: t.members.ROLE_VIEWER_DESC,
    },
  };
  return (role: BaseWorkspaceRole | string) => typeLocaleMap[role];
}

export function useBaseRoles(): { type: BaseWorkspaceRole; name: string; description: string }[] {
  const getBaseRoleLocale = useBaseRoleLocale();
  const { baseWorkspaceRoles } = useRoleStore();
  return baseWorkspaceRoles.map((role) => ({
    type: role,
    ...getBaseRoleLocale(role),
  }));
}

export function useWorkspaceRoleLocale(): (role: WorkspaceRoleWithPermissions) => {
  name: string;
  description: string;
} {
  const { t } = useTranslate();

  const systemRoleMap = {
    meeting_manager: {
      name: t.members.ROLE_MEETING_ADMIN,
      description: t.members.ROLE_MEETING_ADMIN_DESC,
    },
    team_manager: {
      name: t.members.ROLE_TEAM_ADMIN,
      description: t.members.ROLE_TEAM_ADMIN_DESC,
    },
    billing_manager: {
      name: t.members.ROLE_PAYMENT_ADMIN,
      description: t.members.ROLE_PAYMENT_ADMIN_DESC,
    },
  };

  return (role: WorkspaceRoleWithPermissions) => {
    if (role.is_system_role) {
      const systemRole = systemRoleMap[role.name];
      if (systemRole) {
        return systemRole;
      }
    }

    return {
      name: role.name,
      description: '',
    };
  };
}

export function useWorkspaceRoles(): Array<
  WorkspaceRoleWithPermissions & { name: string; description: string }
> {
  const getWorkspaceRoleLocale = useWorkspaceRoleLocale();
  const { workspaceRoles } = useRoleStore();
  return workspaceRoles.map((role) => ({
    ...role,
    ...getWorkspaceRoleLocale(role),
  }));
}

export function useTeamBaseRoleLocale(): (role: BaseTeamRole | string) =>
  | {
      name: string;
      description: string;
    }
  | undefined {
  const { t } = useTranslate();

  const typeLocaleMap = {
    [BaseTeamRole.MANAGER]: {
      name: t.members.TEAM_ROLE_TEAM_LEADER,
      description: t.members.TEAM_ROLE_TEAM_LEADER_DESC,
    },
    [BaseTeamRole.EDITOR]: {
      name: t.members.TEAM_ROLE_TEAM_MEMBER,
      description: t.members.TEAM_ROLE_TEAM_MEMBER_DESC,
    },
    [BaseTeamRole.VIEWER]: {
      name: t.members.TEAM_ROLE_TEAM_VIEWER,
      description: t.members.TEAM_ROLE_TEAM_VIEWER_DESC,
    },
  };

  return (role: BaseTeamRole | string) => typeLocaleMap[role];
}

export function useTeamBaseRoles(): { type: BaseTeamRole; name: string; description: string }[] {
  const getTeamBaseRoleLocale = useTeamBaseRoleLocale();
  const { baseTeamRoles } = useRoleStore();
  return baseTeamRoles.map((role) => ({
    type: role,
    ...getTeamBaseRoleLocale(role),
  }));
}

export function useTeamRoleLocale(): (role: TeamRole) => { name: string; description: string } {
  const { t } = useTranslate();

  const systemRoleMap = {
    team_meeting_manager: {
      name: t.members.TEAM_ROLE_TEAM_MEETING_MANAGER,
      description: t.members.TEAM_ROLE_TEAM_MEETING_MANAGER_DESC,
    },
  };

  return (role: TeamRole) => {
    if (role.is_system_role) {
      const systemRole = systemRoleMap[role.name];
      if (systemRole) {
        return systemRole;
      }
    }

    return {
      name: role.name,
      description: '',
    };
  };
}

export function useTeamRoles(): Array<TeamRole & { name: string; description: string }> {
  const getTeamRoleLocale = useTeamRoleLocale();
  const { teamRoles } = useRoleStore();
  return teamRoles.map((role) => ({
    ...role,
    ...getTeamRoleLocale(role),
  }));
}

export function usePermissionLocale(): (permission: PermissionEnum) => {
  title: string;
  description: string;
} {
  const { t } = useTranslate();
  const permissionDescription: Record<PermissionEnum, { title: string; description: string }> = {
    member_management: {
      title: t.members.PERMISSION_USER_MANAGEMENT_TITLE,
      description: t.members.PERMISSION_USER_MANAGEMENT_DESC,
    },
    workspace_management: {
      title: t.members.PERMISSION_WORKSPACE_MANAGEMENT_TITLE,
      description: t.members.PERMISSION_WORKSPACE_MANAGEMENT_DESC,
    },
    payment_management: {
      title: t.members.PERMISSION_PAYMENT_MANAGEMENT_TITLE,
      description: t.members.PERMISSION_PAYMENT_MANAGEMENT_DESC,
    },
    view_all_meetings: {
      title: t.members.PERMISSION_VIEW_ALL_MEETINGS_TITLE,
      description: t.members.PERMISSION_VIEW_ALL_MEETINGS_DESC,
    },
    delete_meetings_from_workspace: {
      title: t.members.PERMISSION_DELETE_MEETINGS_FROM_WORKSPACE_TITLE,
      description: t.members.PERMISSION_DELETE_MEETINGS_FROM_WORKSPACE_DESC,
    },
    archive_meetings: {
      title: t.members.PERMISSION_ARCHIVE_MEETINGS_TITLE,
      description: t.members.PERMISSION_ARCHIVE_MEETINGS_DESC,
    },
    chatbot_features: {
      title: t.members.PERMISSION_CHATBOT_FEATURES_TITLE,
      description: t.members.PERMISSION_CHATBOT_FEATURES_DESC,
    },
    team_management: {
      title: t.members.PERMISSION_TEAM_MANAGEMENT_TITLE,
      description: t.members.PERMISSION_TEAM_MANAGEMENT_DESC,
    },
    team_member_management: {
      title: t.members.PERMISSION_TEAM_MEMBER_MANAGEMENT_TITLE,
      description: t.members.PERMISSION_TEAM_MEMBER_MANAGEMENT_DESC,
    },
    delete_meetings_from_team: {
      title: t.members.PERMISSION_DELETE_MEETINGS_FROM_TEAM_TITLE,
      description: t.members.PERMISSION_DELETE_MEETINGS_FROM_TEAM_DESC,
    },
    edit_meeting_view_scope: {
      title: t.members.PERMISSION_EDIT_MEETING_VIEW_SCOPE_TITLE,
      description: t.members.PERMISSION_EDIT_MEETING_VIEW_SCOPE_DESC,
    },
    edit_meeting_properties: {
      title: t.members.PERMISSION_EDIT_MEETING_PROPERTIES_TITLE,
      description: t.members.PERMISSION_EDIT_MEETING_PROPERTIES_DESC,
    },
    external_meeting_sharing: {
      title: t.members.PERMISSION_EXTERNAL_MEETING_SHARING_TITLE,
      description: t.members.PERMISSION_EXTERNAL_MEETING_SHARING_DESC,
    },
    reset_language: {
      title: t.members.PERMISSION_RESET_LANGUAGE_TITLE,
      description: t.members.PERMISSION_RESET_LANGUAGE_DESC,
    },
    automation_integration: {
      title: t.members.PERMISSION_SLACK_WEBHOOK_INTEGRATION_TITLE,
      description: t.members.PERMISSION_SLACK_WEBHOOK_INTEGRATION_DESC,
    },
    share_integration: {
      title: t.members.PERMISSION_CRM_INTEGRATION_TITLE,
      description: t.members.PERMISSION_CRM_INTEGRATION_DESC,
    },
    view_meetings: {
      title: t.members.PERMISSION_VIEW_MEETING_LIST_TITLE,
      description: t.members.PERMISSION_VIEW_MEETING_LIST_DESC,
    },
    all_archive_meetings: {
      title: t.members.PERMISSION_ALL_ARCHIVE_MEETINGS_TITLE,
      description: t.members.PERMISSION_ALL_ARCHIVE_MEETINGS_DESC,
    },
    all_edit_meeting_properties: {
      title: t.members.PERMISSION_ALL_EDIT_MEETING_PROPERTIES_TITLE,
      description: t.members.PERMISSION_ALL_EDIT_MEETING_PROPERTIES_DESC,
    },
    all_external_meeting_sharing: {
      title: t.members.PERMISSION_ALL_EXTERNAL_MEETING_SHARING_TITLE,
      description: t.members.PERMISSION_ALL_EXTERNAL_MEETING_SHARING_DESC,
    },
    all_share_integration: {
      title: t.members.PERMISSION_ALL_SHARE_INTEGRATION_TITLE,
      description: t.members.PERMISSION_ALL_SHARE_INTEGRATION_DESC,
    },
    workspace_integration: {
      title: t.members.PERMISSION_WORKSPACE_INTEGRATION_TITLE,
      description: t.members.PERMISSION_WORKSPACE_INTEGRATION_DESC,
    },
    view_all_team_meetings: {
      title: t.members.PERMISSION_VIEW_ALL_TEAM_MEETINGS_TITLE,
      description: t.members.PERMISSION_VIEW_ALL_TEAM_MEETINGS_DESC,
    },
  };

  return (permission: PermissionEnum) => permissionDescription[permission];
}

export function useGetRoleLabel() {
  const getBaseRoleLocale = useBaseRoleLocale();
  const getWorkspaceRoleLocale = useWorkspaceRoleLocale();
  return ({
    baseRole,
    workspaceRoles,
  }: {
    baseRole: BaseWorkspaceRole;
    workspaceRoles: WorkspaceRoleWithPermissions[];
  }) => {
    const baseRoleLocale = getBaseRoleLocale(baseRole);
    if (!baseRoleLocale) {
      return '';
    }

    return [
      baseRoleLocale.name,
      ...workspaceRoles.map((role) => getWorkspaceRoleLocale(role).name),
    ].join(', ');
  };
}
export function useGetTeamRoleLabel() {
  const getTeamBaseRoleLocale = useTeamBaseRoleLocale();
  const getTeamRoleLocale = useTeamRoleLocale();
  return ({ baseRole, teamRoles }: { baseRole: BaseTeamRole; teamRoles: TeamRole[] }) => {
    const baseRoleLocale = getTeamBaseRoleLocale(baseRole);
    if (!baseRoleLocale) {
      return '';
    }

    return [baseRoleLocale.name, ...teamRoles.map((role) => getTeamRoleLocale(role).name)].join(
      ', '
    );
  };
}
